import { COLORS, CONFIG } from "../config";
import { drawClef } from "./clef-images";
import type { StaffFrameModel, StaffNoteState } from "./staff";

const STATE_COLORS: Record<StaffNoteState, string> = {
  future: COLORS.staffTarget,
  active: COLORS.staffTarget,
  hit: COLORS.staffHit,
  miss: COLORS.staffHistory,
  wrong: COLORS.staffWrong,
};

/** Blit the draw-model: staff lines, clefs, barlines, noteheads (with value
 *  glyphs + beams), now-marker. Thin canvas pass over the pure `staffFrame` model. */
export function drawStaff(ctx: CanvasRenderingContext2D, model: StaffFrameModel): void {
  const lineGap = CONFIG.staff.lineGapPx;
  ctx.save();
  ctx.lineWidth = 1;
  for (const box of model.systems) {
    for (const midY of [box.trebleMiddleY, box.bassMiddleY]) {
      ctx.strokeStyle = COLORS.staffLine;
      ctx.beginPath();
      for (let i = -2; i <= 2; i++) {
        const y = midY + i * lineGap;
        ctx.moveTo(box.x0, y);
        ctx.lineTo(box.x1, y);
      }
      ctx.stroke();
    }
    // Vector clefs (SVG → tinted raster), so they render without a music font.
    const clefRight = box.x0 - 6;
    drawClef(
      ctx,
      "treble",
      clefRight,
      box.trebleMiddleY + lineGap * 0.5,
      lineGap * 6.6,
      COLORS.staffClef,
    );
    drawClef(
      ctx,
      "bass",
      clefRight,
      box.bassMiddleY - lineGap * 0.4,
      lineGap * 3.8,
      COLORS.staffClef,
    );
  }
  // Measure barlines, drawn under the noteheads.
  ctx.strokeStyle = COLORS.staffLine;
  ctx.lineWidth = 1;
  ctx.beginPath();
  for (const b of model.barlines) {
    ctx.moveTo(b.x, b.y0);
    ctx.lineTo(b.x, b.y1);
  }
  ctx.stroke();

  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  const r = Math.max(2.5, lineGap / 2 - 0.5);
  const ledgerHalf = r + 3;
  for (const n of model.noteheads) {
    ctx.globalAlpha = Math.max(0, Math.min(1, n.alpha));
    ctx.fillStyle = STATE_COLORS[n.state];
    // Ledger lines (in the note's colour, so they read as part of that note).
    if (n.ledgers.length > 0) {
      ctx.strokeStyle = STATE_COLORS[n.state];
      ctx.lineWidth = 1.5;
      ctx.beginPath();
      for (const ly of n.ledgers) {
        ctx.moveTo(n.x - ledgerHalf, ly);
        ctx.lineTo(n.x + ledgerHalf, ly);
      }
      ctx.stroke();
    }
    // Notehead: open (stroked) for whole/half + miss; filled otherwise.
    const open = n.value === "whole" || n.value === "half";
    if (n.state === "miss" || open) {
      ctx.strokeStyle = STATE_COLORS[n.state];
      ctx.lineWidth = open ? 1.6 : 1.5;
      ctx.beginPath();
      ctx.ellipse(n.x, n.y, r, r * 0.8, 0, 0, Math.PI * 2);
      ctx.stroke();
    } else {
      ctx.beginPath();
      ctx.ellipse(n.x, n.y, r, r * 0.8, 0, 0, Math.PI * 2);
      ctx.fill();
    }
    // Stem + flags for everything except whole / unknown. Beamed notes draw their
    // stem to the beam level and skip the flag (the beam replaces it).
    if (n.value && n.value !== "whole") {
      if (n.beamed && n.stemTipY !== null) {
        const up = n.stemTipY < n.y;
        const sx = n.x + (up ? r * 0.95 : -r * 0.95);
        ctx.strokeStyle = STATE_COLORS[n.state];
        ctx.lineWidth = 1.4;
        ctx.beginPath();
        ctx.moveTo(sx, n.y);
        ctx.lineTo(sx, n.stemTipY);
        ctx.stroke();
      } else {
        const up = n.y >= n.middleY; // below the middle line → stem up
        const sx = n.x + (up ? r * 0.95 : -r * 0.95);
        const tipY = up
          ? n.y - lineGap * CONFIG.staff.stemLenGaps
          : n.y + lineGap * CONFIG.staff.stemLenGaps;
        ctx.strokeStyle = STATE_COLORS[n.state];
        ctx.lineWidth = 1.4;
        ctx.beginPath();
        ctx.moveTo(sx, n.y);
        ctx.lineTo(sx, tipY);
        ctx.stroke();
        const flags = n.value === "eighth" ? 1 : n.value === "sixteenth" ? 2 : 0;
        const flagLen = lineGap * CONFIG.staff.flagLenGaps;
        const flagGap = lineGap * CONFIG.staff.flagGapGaps;
        for (let k = 0; k < flags; k++) {
          const fy = tipY + (up ? k * flagGap : -k * flagGap);
          ctx.beginPath();
          ctx.moveTo(sx, fy);
          ctx.lineTo(sx + flagLen, fy + (up ? flagLen * 0.9 : -flagLen * 0.9));
          ctx.stroke();
        }
      }
    }
    // Augmentation dot, nudged off a staff line into the adjacent space.
    if (n.dotted) {
      const onLine = Math.round((n.middleY - n.y) / (lineGap / 2)) % 2 === 0;
      ctx.beginPath();
      ctx.arc(
        n.x + r + CONFIG.staff.dotOffsetPx,
        n.y - (onLine ? lineGap / 2 : 0),
        CONFIG.staff.dotRadiusPx,
        0,
        Math.PI * 2,
      );
      ctx.fill();
    }
    if (n.accidental) {
      ctx.font = "600 10px system-ui, sans-serif";
      ctx.fillText(
        n.accidental === "sharp" ? "♯" : n.accidental === "flat" ? "♭" : "♮",
        n.x - r - 4,
        n.y,
      );
    }
  }
  // Beams over grouped runs (slanted primary + optional sixteenth secondary), in
  // the group's state colour. Clipped to the staff block so a group whose members
  // span off-screen (flow scrolling) doesn't draw past the staff edges.
  const blockX0 = model.systems.length ? Math.min(...model.systems.map((s) => s.x0)) : 0;
  const blockX1 = model.systems.length ? Math.max(...model.systems.map((s) => s.x1)) : 0;
  for (const bm of model.beams) {
    const slope = bm.x1 !== bm.x0 ? (bm.y1 - bm.y0) / (bm.x1 - bm.x0) : 0;
    const yAt = (x: number): number => bm.y0 + slope * (x - bm.x0);
    ctx.globalAlpha = bm.alpha;
    ctx.strokeStyle = STATE_COLORS[bm.state];
    ctx.lineWidth = CONFIG.staff.beamWidthPx;
    const px0 = Math.max(bm.x0, blockX0);
    const px1 = Math.min(bm.x1, blockX1);
    if (px1 > px0) {
      ctx.beginPath();
      ctx.moveTo(px0, yAt(px0));
      ctx.lineTo(px1, yAt(px1));
      ctx.stroke();
    }
    if (bm.secondary) {
      const dy = (bm.up ? 1 : -1) * lineGap * CONFIG.staff.beamSpanGaps;
      const sx0 = Math.max(bm.secondary.x0, blockX0);
      const sx1 = Math.min(bm.secondary.x1, blockX1);
      if (sx1 > sx0) {
        ctx.beginPath();
        ctx.moveTo(sx0, yAt(sx0) + dy);
        ctx.lineTo(sx1, yAt(sx1) + dy);
        ctx.stroke();
      }
    }
  }
  ctx.globalAlpha = 1;
  // Lead-sheet chord symbols above the treble staff (own pass — independent of
  // note-head alpha, so the cue survives the recall fade).
  if (model.chordSymbols.length > 0) {
    ctx.font = "600 13px system-ui, sans-serif";
    ctx.textAlign = "left";
    ctx.textBaseline = "middle";
    ctx.fillStyle = COLORS.staffClef;
    for (const s of model.chordSymbols) {
      ctx.globalAlpha = Math.max(0, Math.min(1, s.alpha));
      ctx.fillText(s.text, s.x + 2, s.y);
    }
    ctx.globalAlpha = 1;
  }
  if (model.nowMarker) {
    ctx.strokeStyle = COLORS.hitLine;
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(model.nowMarker.x, model.nowMarker.y0);
    ctx.lineTo(model.nowMarker.x, model.nowMarker.y1);
    ctx.stroke();
  }
  ctx.restore();
}
